import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import { subscribeToTicketEvents } from '../utils/ticketEventStream';
import { apiClient } from '../utils/apiClient';
import { cleanEmailText } from '../utils/emailText';
import NotificationToggle from '../components/NotificationToggle';
import MyFollowUps from '../components/MyFollowUps';
import '../styles/ticketTheme.css';
import { downloadCsv } from '../utils/csv';
import { ticketsToCsv, ticketCsvFilename } from '../utils/ticketCsv';
import './Tickets.css';
import { alertDialog } from '../utils/dialogs';

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  pending: 'Awaiting Reply from User',
  resolved: 'Resolved',
  closed: 'Closed'
};

const BULK_ACTION_COPY = {
  close: { title: 'Close Tickets', body: (n) => `Close ${n} ticket${n === 1 ? '' : 's'}?` },
  junk: { title: 'Mark as Junk', body: (n) => `Close ${n} ticket${n === 1 ? '' : 's'} and blacklist the sender(s) so they can't create new tickets?` },
  delete: { title: 'Delete Tickets', body: (n) => `Permanently delete ${n} ticket${n === 1 ? '' : 's'}? This cannot be undone.` }
};

// Left-hand "Views" panel: one entry per status, same filter state as before.
const VIEW_TABS = [
  { value: 'all', label: 'All Tickets', sub: 'Everything in this view', icon: 'fa-inbox' },
  { value: 'open', label: 'Open', sub: 'Awaiting action', icon: 'fa-clock' },
  { value: 'in_progress', label: 'In Progress', sub: 'Being worked on', icon: 'fa-spinner' },
  { value: 'pending', label: 'Awaiting Reply', sub: 'Waiting on the customer', icon: 'fa-hourglass-half' },
  { value: 'resolved', label: 'Resolved', sub: 'Recently resolved', icon: 'fa-circle-check' },
  { value: 'closed', label: 'Closed', sub: 'Resolved and closed', icon: 'fa-box-archive' }
];

const VISIBLE_COLUMNS_KEY = 'tickets-visible-columns';
const TOGGLEABLE_COLUMNS = ['customer', 'status', 'priority', 'assigned', 'tags', 'created'];

const loadVisibleColumns = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(VISIBLE_COLUMNS_KEY));
    if (Array.isArray(saved)) return new Set(saved);
  } catch {
    // ignore malformed/missing storage, fall through to the default
  }
  return new Set(TOGGLEABLE_COLUMNS);
};

const EMPTY_FILTERS = {
  statusFilter: 'all',
  priorityFilter: 'all',
  assigneeFilter: 'all',
  dueFilter: 'all',
  categoryFilter: '',
  tagFilter: 'all',
  createdFrom: '',
  createdTo: ''
};

function Tickets() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [staff, setStaff] = useState([]);
  // "overview" = open/unassigned + assigned to me (default triage view);
  // "all" = every ticket, unfiltered by assignment. A status filter coming
  // in from elsewhere (e.g. Home's "Open Tickets" widget) implies the user
  // wants to see everything with that status, not just their own slice.
  const [viewMode, setViewMode] = useState(() => (new URLSearchParams(window.location.search).get('status') ? 'all' : 'overview'));
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [groupBy, setGroupBy] = useState('none');
  const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState(null);
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkPriority, setBulkPriority] = useState('medium');
  const [savedViews, setSavedViews] = useState([]);
  const [showViewsMenu, setShowViewsMenu] = useState(false);
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [savingView, setSavingView] = useState(false);
  const [focusedTicketId, setFocusedTicketId] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  // Phones: the filter bar and toolbar start collapsed behind a Filters button.
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const statusFromUrl = urlParams.get('status');
    const assignedFromUrl = urlParams.get('assigned');
    const dueFromUrl = urlParams.get('due');
    if (statusFromUrl || assignedFromUrl || dueFromUrl) {
      setFilters((prev) => ({
        ...prev,
        ...(statusFromUrl ? { statusFilter: statusFromUrl } : {}),
        ...(assignedFromUrl ? { assigneeFilter: assignedFromUrl } : {}),
        ...(dueFromUrl ? { dueFilter: dueFromUrl } : {})
      }));
    }

    fetchTickets(viewMode);

    apiClient.post('/api/users/findall')
      .then((data) => setStaff(Array.isArray(data) ? data.filter((u) => (u.role_type || '').toLowerCase() !== 'customer') : []))
      .catch(() => setStaff([]));

    apiClient.get('/api/ticket-saved-views')
      .then((data) => setSavedViews(Array.isArray(data) ? data : []))
      .catch(() => setSavedViews([]));

    // Live-refresh the list when anything ticket-related happens
    // elsewhere - 'stream-open' covers both first connect and any
    // reconnect, since events fired during a drop are gone for good.
    const unsubscribe = subscribeToTicketEvents((event) => {
      if (['stream-open', 'ticket-created', 'ticket-updated', 'ticket-activity', 'tickets-updated'].includes(event.type)) {
        fetchTickets(viewMode);
      }
    });
    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.search, viewMode]);

  useEffect(() => {
    try { localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify([...visibleColumns])); } catch { /* best-effort only */ }
  }, [visibleColumns]);

  const fetchTickets = async (mode) => {
    setLoading(true);
    try {
      const endpoint = mode === 'overview' ? '/api/tickets/overview' : '/api/tickets';
      const data = await apiClient.get(endpoint);
      setTickets(data);
      setSelectedIds(new Set());
    } catch (err) {
      setError('Error fetching tickets');
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'status-open';
      case 'in-progress':
      case 'in progress':
      case 'in_progress': return 'status-in-progress';
      case 'pending': return 'status-pending';
      case 'resolved': return 'status-resolved';
      case 'closed': return 'status-closed';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-default';
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-default';
    }
  };

  // Age in whole days since creation - the "ticket age" column, plus the
  // same overdue/soon logic TicketDetail.js uses for due_date, so the two
  // screens agree on what "overdue" means without sharing a module for
  // what's an 8-line calculation used in exactly two places.
  const getAgeDays = (createdAt) => {
    if (!createdAt) return null;
    return Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
  };

  const isOverdue = (ticket) => ticket.due_date && !['resolved', 'closed'].includes(ticket.status) && new Date(ticket.due_date) < new Date();
  const isDueToday = (ticket) => {
    if (!ticket.due_date || ['resolved', 'closed'].includes(ticket.status)) return false;
    const due = new Date(ticket.due_date);
    const now = new Date();
    return due.toDateString() === now.toDateString() && due >= now;
  };

  const getDueInfo = (ticket) => {
    if (isOverdue(ticket)) return { label: 'Overdue', className: 'due-overdue' };
    if (isDueToday(ticket)) return { label: 'Due soon', className: 'due-soon' };
    return null;
  };

  // Every tag seen across the currently-loaded ticket set - the tag filter's
  // option list is derived from real data rather than a separate lookup call.
  const allTags = useMemo(() => {
    const set = new Set();
    tickets.forEach((t) => (Array.isArray(t.tags) ? t.tags : []).forEach((tag) => set.add(tag)));
    return [...set].sort();
  }, [tickets]);

  const ticketPassesFilters = (ticket, f) => {
    const matchesSearch = searchTerm === '' ||
      ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.ticket_id?.toString().includes(searchTerm) ||
      ticket.ticket_number?.toString().includes(searchTerm) ||
      ticket.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = f.statusFilter === 'all' || ticket.status?.toLowerCase() === f.statusFilter.toLowerCase();
    const matchesPriority = f.priorityFilter === 'all' || ticket.priority?.toLowerCase() === f.priorityFilter.toLowerCase();
    const matchesAssignee = f.assigneeFilter === 'all' ||
      (f.assigneeFilter === 'unassigned' ? !ticket.assigned_user_id : String(ticket.assigned_user_id) === f.assigneeFilter);
    const matchesDue = f.dueFilter === 'all' ||
      (f.dueFilter === 'overdue' ? isOverdue(ticket) : f.dueFilter === 'today' ? isDueToday(ticket) : true);
    const matchesCategory = !f.categoryFilter.trim() ||
      (ticket.category || '').toLowerCase().includes(f.categoryFilter.trim().toLowerCase());
    const matchesTag = f.tagFilter === 'all' || (Array.isArray(ticket.tags) && ticket.tags.includes(f.tagFilter));
    const createdAt = ticket.created_at ? new Date(ticket.created_at) : null;
    const matchesFrom = !f.createdFrom || (createdAt && createdAt >= new Date(f.createdFrom));
    const matchesTo = !f.createdTo || (createdAt && createdAt <= new Date(`${f.createdTo}T23:59:59`));

    return matchesSearch && matchesStatus && matchesPriority && matchesAssignee && matchesDue && matchesCategory && matchesTag && matchesFrom && matchesTo;
  };

  const filteredTickets = tickets.filter((ticket) => ticketPassesFilters(ticket, filters));

  // Tab counts honour every other filter and search, but not the status itself,
  // so each tab shows what you'd get by clicking it.
  const statusTabCounts = useMemo(() => {
    const counts = { all: 0 };
    Object.keys(STATUS_LABELS).forEach((k) => { counts[k] = 0; });
    tickets.forEach((t) => {
      if (!ticketPassesFilters(t, { ...filters, statusFilter: 'all' })) return;
      counts.all += 1;
      const key = (t.status || '').toLowerCase();
      if (key in counts) counts[key] += 1;
    });
    return counts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, filters, searchTerm]);

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => value && value !== EMPTY_FILTERS[key]).length;

  const confirmBulkAction = async () => {
    const action = pendingBulkAction;
    const ids = [...selectedIds];
    if (!action || ids.length === 0) return;

    setBulkWorking(true);
    try {
      const body = { ticket_ids: ids, action };
      if (action === 'assign') body.assigned_user_id = bulkAssignee || null;
      if (action === 'priority') body.priority = bulkPriority;
      const data = await apiClient.put('/api/tickets/bulk', body);
      if (action === 'junk' && data.blacklisted > 0) {
        alertDialog(`Closed ${data.updated} ticket(s) and blacklisted ${data.blacklisted} sender(s).`);
      }
      fetchTickets(viewMode);
    } catch (err) {
      console.error('Error running bulk action:', err);
      alertDialog(err.message || 'Bulk action failed');
    } finally {
      setBulkWorking(false);
      setPendingBulkAction(null);
    }
  };

  const openBulkModal = (action) => {
    setBulkAssignee('');
    setBulkPriority('medium');
    setPendingBulkAction(action);
  };

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const applySavedView = (view) => {
    setFilters({ ...EMPTY_FILTERS, ...view.filters?.filters });
    if (view.filters?.viewMode) setViewMode(view.filters.viewMode);
    setShowViewsMenu(false);
  };

  const saveCurrentView = async (e) => {
    e.preventDefault();
    if (!newViewName.trim()) return;
    setSavingView(true);
    try {
      const created = await apiClient.post('/api/ticket-saved-views', {
        name: newViewName.trim(),
        filters: { viewMode, filters }
      });
      setSavedViews((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewViewName('');
      setShowSaveViewModal(false);
    } catch (err) {
      alertDialog(err.message || 'Failed to save view');
    } finally {
      setSavingView(false);
    }
  };

  const deleteSavedView = async (view) => {
    try {
      await apiClient.delete(`/api/ticket-saved-views/${view.id}`);
      setSavedViews((prev) => prev.filter((v) => v.id !== view.id));
    } catch (err) {
      alertDialog(err.message || 'Failed to delete view');
    }
  };

  const allColumns = [
    { key: 'id', label: 'Ticket #', sortable: true, sortValue: (t) => t.ticket_number || 0, render: (t) => `#${t.ticket_number}` },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      sortValue: (t) => t.subject || '',
      render: (t) => (
        <div>
          <div className="subject-text">{t.subject || 'No Subject'}</div>
          {t.description && (
            <div className="description-preview">
              {(() => {
                const text = cleanEmailText(t.description).reply;
                return text.length > 100 ? `${text.substring(0, 100)}…` : text;
              })()}
            </div>
          )}
        </div>
      )
    },
    { key: 'customer', label: 'Customer', sortable: true, sortValue: (t) => t.customer_name || '', render: (t) => t.customer_name || 'Unknown' },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      sortValue: (t) => STATUS_LABELS[t.status] || t.status || '',
      render: (t) => <span className={`status-badge ${getStatusClass(t.status)}`}>{STATUS_LABELS[t.status] || t.status || 'Unknown'}</span>
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      sortValue: (t) => t.priority || '',
      render: (t) => <span className={`priority-badge ${getPriorityClass(t.priority)}`}>{t.priority || 'Normal'}</span>
    },
    { key: 'assigned', label: 'Assigned To', sortable: true, sortValue: (t) => t.assigned_user_name || '', render: (t) => t.assigned_user_name || 'Unassigned' },
    {
      key: 'tags',
      label: 'Tags',
      render: (t) => {
        const tags = Array.isArray(t.tags) ? t.tags : [];
        if (tags.length === 0) return null;
        return (
          <div className="ticket-row-tags">
            {tags.slice(0, 2).map((tag) => <span key={tag} className="ticket-row-tag">{tag}</span>)}
            {tags.length > 2 && <span className="ticket-row-tag-more">+{tags.length - 2}</span>}
          </div>
        );
      }
    },
    {
      key: 'created',
      label: 'Age',
      sortable: true,
      sortValue: (t) => t.created_at ? new Date(t.created_at).getTime() : 0,
      render: (t) => {
        const days = getAgeDays(t.created_at);
        const due = getDueInfo(t);
        return (
          <div title={`Created ${formatDate(t.created_at)}`}>
            <div>{days === null ? 'N/A' : days === 0 ? 'Today' : `${days}d`}</div>
            {due && <span className={`due-chip ${due.className}`}>{due.label}</span>}
          </div>
        );
      }
    }
  ];

  const columns = allColumns.filter((c) => c.key === 'id' || c.key === 'subject' || visibleColumns.has(c.key));

  // Groups are rendered as separate tables sharing one selection Set, so the
  // bulk bar's count still reflects everything selected across every group.
  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ label: null, rows: filteredTickets }];
    if (groupBy === 'status') {
      return Object.keys(STATUS_LABELS)
        .map((status) => ({ label: STATUS_LABELS[status], rows: filteredTickets.filter((t) => t.status === status) }))
        .filter((g) => g.rows.length > 0);
    }
    const byAssignee = new Map();
    filteredTickets.forEach((t) => {
      const key = t.assigned_user_name || 'Unassigned';
      if (!byAssignee.has(key)) byAssignee.set(key, []);
      byAssignee.get(key).push(t);
    });
    return [...byAssignee.entries()]
      .sort(([a], [b]) => (a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b)))
      .map(([label, rows]) => ({ label, rows }));
  }, [groupBy, filteredTickets]);

  // Keyboard navigation over whatever order is on screen (grouped or not).
  // Ignored while typing in a field or with a modal open, so it never
  // swallows keystrokes meant for a form.
  const modalOpen = !!pendingBulkAction || showSaveViewModal || showShortcuts;
  useEffect(() => {
    const flat = groups.flatMap((g) => g.rows);
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || modalOpen) return;
      const tag = e.target.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || e.target.isContentEditable) return;

      const index = flat.findIndex((t) => t.ticket_id === focusedTicketId);
      if (e.key === 'j' && flat.length) {
        setFocusedTicketId(flat[Math.min(index + 1, flat.length - 1)].ticket_id);
      } else if (e.key === 'k' && flat.length) {
        setFocusedTicketId(flat[Math.max(index - 1, 0)].ticket_id);
      } else if ((e.key === 'Enter' || e.key === 'o') && index >= 0 && tag !== 'BUTTON' && tag !== 'A') {
        navigate(`/tickets/${flat[index].ticket_id}`);
      } else if (e.key === '?') {
        setShowShortcuts(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [groups, focusedTicketId, modalOpen, navigate]);

  useEffect(() => {
    document.querySelector('.ticket-row-focused')?.scrollIntoView({ block: 'nearest' });
  }, [focusedTicketId]);

  return (
    <PageShell contentClassName="tk">
      <div className="tickets-header">
        <div className="tickets-header-main">
          <h1>{viewMode === 'overview' ? 'Ticket Overview' : 'All Tickets'}</h1>
          <span className="stat">{viewMode === 'overview' ? 'Showing' : 'Total'}: {tickets.length}</span>
        </div>
        <div className="tickets-header-tools">
        <MyFollowUps />
        <NotificationToggle />
        <div className="tickets-view-toggle">
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'overview' ? 'active' : ''}`}
            onClick={() => setViewMode('overview')}
          >
            My Overview
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'all' ? 'active' : ''}`}
            onClick={() => setViewMode('all')}
          >
            All Tickets
          </button>
        </div>
        </div>
      </div>

      {viewMode === 'overview' && (
        <p className="tickets-overview-hint">
          Open tickets that are unassigned, plus tickets assigned to you. Tickets someone else is already working on aren't shown here.
        </p>
      )}

      <div className="tickets-layout">
        <aside className="tickets-views" aria-label="Ticket views">
          <div className="tickets-views-head">
            <h2>Views</h2>
            <p>Select a view to filter tickets</p>
          </div>
          <div className="tickets-status-tabs" role="tablist" aria-label="Filter by status">
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={filters.statusFilter === tab.value}
                className={`tickets-status-tab ${filters.statusFilter === tab.value ? 'active' : ''}`}
                onClick={() => setFilters({ ...filters, statusFilter: tab.value })}
              >
                <i className={`fas ${tab.icon} tickets-status-tab-icon`} aria-hidden="true"></i>
                <span className="tickets-status-tab-text">
                  <span className="tickets-status-tab-name">{tab.label}</span>
                  <span className="tickets-status-tab-sub">{tab.sub}</span>
                </span>
                <span className="tickets-status-tab-count">{statusTabCounts[tab.value] ?? 0}</span>
              </button>
            ))}
          </div>
        </aside>

        <div className="tickets-main">
      <div className={`tickets-toolbar ${showFilters ? 'is-open' : ''}`}>
        <div className="tickets-views-menu">
          <Button variant="secondary" onClick={() => setShowViewsMenu((v) => !v)}>
            <i className="fas fa-bookmark" aria-hidden="true"></i> Views
          </Button>
          {showViewsMenu && (
            <div className="tickets-views-dropdown">
              {savedViews.length === 0 && <p className="tickets-views-empty">No saved views yet.</p>}
              {savedViews.map((view) => (
                <div key={view.id} className="tickets-views-item">
                  <button type="button" onClick={() => applySavedView(view)}>{view.name}</button>
                  <button type="button" className="tickets-views-delete" onClick={() => deleteSavedView(view)} aria-label={`Delete saved view ${view.name}`}>&times;</button>
                </div>
              ))}
              <button type="button" className="tickets-views-save" onClick={() => { setShowViewsMenu(false); setShowSaveViewModal(true); }}>
                <i className="fas fa-plus" aria-hidden="true"></i> Save current filters&hellip;
              </button>
            </div>
          )}
        </div>

        <Button
          variant="secondary"
          onClick={() => downloadCsv(ticketCsvFilename(), ticketsToCsv(filteredTickets, STATUS_LABELS))}
          disabled={filteredTickets.length === 0}
          title={`Download the ${filteredTickets.length} ticket${filteredTickets.length === 1 ? '' : 's'} currently shown as a spreadsheet`}
        >
          <i className="fas fa-file-csv" aria-hidden="true"></i> Export
        </Button>

        <div className="tickets-column-picker">
          <Button variant="secondary" onClick={() => setShowColumnPicker((v) => !v)}>
            <i className="fas fa-table-columns" aria-hidden="true"></i> Columns
          </Button>
          {showColumnPicker && (
            <div className="tickets-column-dropdown">
              {TOGGLEABLE_COLUMNS.map((key) => {
                const col = allColumns.find((c) => c.key === key);
                return (
                  <label key={key}>
                    <input type="checkbox" checked={visibleColumns.has(key)} onChange={() => toggleColumn(key)} />
                    {col.label}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="filter-group">
          <label htmlFor="group-by">Group by:</label>
          <select id="group-by" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="filter-select">
            <option value="none">No grouping</option>
            <option value="status">Status</option>
            <option value="assigned">Assignee</option>
          </select>
        </div>
      </div>

      <div className={`tickets-filters ${showFilters ? 'is-open' : ''}`}>
        <button
          type="button"
          className="tickets-filters-toggle"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((v) => !v)}
        >
          <i className="fas fa-sliders" aria-hidden="true"></i> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </button>
        <div className="filter-group filter-group-search">
          <label htmlFor="ticket-search">Search</label>
          <input
            id="ticket-search"
            type="text"
            aria-label="Search tickets"
            placeholder="Search tickets…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="priority-filter">Priority:</label>
          <select id="priority-filter" value={filters.priorityFilter} onChange={(e) => setFilters({ ...filters, priorityFilter: e.target.value })} className="filter-select">
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="assignee-filter">Assignee:</label>
          <select id="assignee-filter" value={filters.assigneeFilter} onChange={(e) => setFilters({ ...filters, assigneeFilter: e.target.value })} className="filter-select">
            <option value="all">Anyone</option>
            <option value="unassigned">Unassigned</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="due-filter">Due:</label>
          <select id="due-filter" value={filters.dueFilter} onChange={(e) => setFilters({ ...filters, dueFilter: e.target.value })} className="filter-select">
            <option value="all">Any time</option>
            <option value="overdue">Overdue</option>
            <option value="today">Due today</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="category-filter">Category:</label>
          <input
            id="category-filter"
            type="text"
            placeholder="e.g. Billing…"
            value={filters.categoryFilter}
            onChange={(e) => setFilters({ ...filters, categoryFilter: e.target.value })}
            className="filter-select"
          />
        </div>

        {allTags.length > 0 && (
          <div className="filter-group">
            <label htmlFor="tag-filter">Tag:</label>
            <select id="tag-filter" value={filters.tagFilter} onChange={(e) => setFilters({ ...filters, tagFilter: e.target.value })} className="filter-select">
              <option value="all">Any tag</option>
              {allTags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
            </select>
          </div>
        )}

        <div className="filter-group filter-group-daterange">
          <label htmlFor="created-from">Created:</label>
          <input id="created-from" type="date" value={filters.createdFrom} onChange={(e) => setFilters({ ...filters, createdFrom: e.target.value })} className="filter-select date-input" />
          <span aria-hidden="true">&ndash;</span>
          <label htmlFor="created-to" className="sr-only">Created to</label>
          <input id="created-to" type="date" value={filters.createdTo} onChange={(e) => setFilters({ ...filters, createdTo: e.target.value })} className="filter-select date-input" />
        </div>

        {activeFilterCount > 0 && (
          <button type="button" className="tickets-clear-filters" onClick={() => setFilters(EMPTY_FILTERS)}>
            Clear {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="tickets-bulk-bar">
          <span>{selectedIds.size} selected</span>
          <Button variant="secondary" onClick={() => openBulkModal('assign')} disabled={bulkWorking}>Reassign</Button>
          <Button variant="secondary" onClick={() => openBulkModal('priority')} disabled={bulkWorking}>Change Priority</Button>
          <Button variant="secondary" onClick={() => openBulkModal('close')} disabled={bulkWorking}>Close Selected</Button>
          <Button variant="danger" onClick={() => openBulkModal('junk')} disabled={bulkWorking}>Mark as Junk</Button>
          <Button variant="danger" onClick={() => openBulkModal('delete')} disabled={bulkWorking}>Delete Selected</Button>
          <Button variant="secondary" onClick={() => setSelectedIds(new Set())} disabled={bulkWorking}>Clear</Button>
        </div>
      )}

      {loading ? (
        <div className="tickets-skeleton" aria-live="polite" aria-busy="true">
          <span className="sr-only">Loading tickets&hellip;</span>
          {[0, 1, 2, 3, 4, 5].map((n) => <div key={n} className="tickets-skeleton-row" />)}
        </div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : filteredTickets.length === 0 ? (
        <ResponsiveTable
          columns={columns}
          rows={[]}
          rowKey="ticket_id"
          emptyMessage={activeFilterCount > 0 || searchTerm ? 'No tickets match your current filters.' : 'No tickets found.'}
        />
      ) : (
        <div className="tickets-groups">
          {groups.map((group) => (
            <div key={group.label || 'all'} className="tickets-group">
              {group.label && <h3 className="tickets-group-heading">{group.label} <span className="tickets-group-count">({group.rows.length})</span></h3>}
              <ResponsiveTable
                columns={columns}
                rows={group.rows}
                rowKey="ticket_id"
                onRowClick={(t) => navigate(`/tickets/${t.ticket_id}`)}
                getRowClassName={(t) => (t.ticket_id === focusedTicketId ? 'ticket-row-focused' : '')}
                emptyMessage="No tickets in this group."
                selectable
                selectedKeys={selectedIds}
                onSelectionChange={setSelectedIds}
              />
            </div>
          ))}
        </div>
      )}

        </div>
      </div>

      <Modal
        open={!!pendingBulkAction && !['assign', 'priority'].includes(pendingBulkAction)}
        onClose={() => setPendingBulkAction(null)}
        title={pendingBulkAction ? BULK_ACTION_COPY[pendingBulkAction]?.title : ''}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setPendingBulkAction(null)} disabled={bulkWorking}>Cancel</Button>
            <Button variant="danger" onClick={confirmBulkAction} disabled={bulkWorking}>{bulkWorking ? 'Working…' : 'Confirm'}</Button>
          </>
        )}
      >
        <p>{pendingBulkAction && BULK_ACTION_COPY[pendingBulkAction] ? BULK_ACTION_COPY[pendingBulkAction].body(selectedIds.size) : ''}</p>
      </Modal>

      <Modal
        open={pendingBulkAction === 'assign'}
        onClose={() => setPendingBulkAction(null)}
        title="Reassign Tickets"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setPendingBulkAction(null)} disabled={bulkWorking}>Cancel</Button>
            <Button onClick={confirmBulkAction} disabled={bulkWorking}>{bulkWorking ? 'Working…' : `Reassign ${selectedIds.size}`}</Button>
          </>
        )}
      >
        <FormField id="bulk-assignee" label="Assign to">
          <select id="bulk-assignee" value={bulkAssignee} onChange={(e) => setBulkAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </select>
        </FormField>
      </Modal>

      <Modal
        open={pendingBulkAction === 'priority'}
        onClose={() => setPendingBulkAction(null)}
        title="Change Priority"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setPendingBulkAction(null)} disabled={bulkWorking}>Cancel</Button>
            <Button onClick={confirmBulkAction} disabled={bulkWorking}>{bulkWorking ? 'Working…' : `Update ${selectedIds.size}`}</Button>
          </>
        )}
      >
        <FormField id="bulk-priority" label="Priority">
          <select id="bulk-priority" value={bulkPriority} onChange={(e) => setBulkPriority(e.target.value)}>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </FormField>
      </Modal>

      <Modal
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
        title="Keyboard Shortcuts"
        footer={<Button onClick={() => setShowShortcuts(false)}>Done</Button>}
      >
        <dl className="shortcut-list">
          <dt><kbd>j</kbd> / <kbd>k</kbd></dt><dd>Move down / up the ticket list</dd>
          <dt><kbd>Enter</kbd> or <kbd>o</kbd></dt><dd>Open the highlighted ticket</dd>
          <dt><kbd>?</kbd></dt><dd>Show this help</dd>
        </dl>
      </Modal>

      <Modal
        open={showSaveViewModal}
        onClose={() => setShowSaveViewModal(false)}
        title="Save Current Filters"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowSaveViewModal(false)}>Cancel</Button>
            <Button type="submit" form="save-view-form" disabled={savingView || !newViewName.trim()}>{savingView ? 'Saving…' : 'Save View'}</Button>
          </>
        )}
      >
        <form id="save-view-form" onSubmit={saveCurrentView}>
          <FormField id="view-name" label="Name" required>
            <input id="view-name" type="text" placeholder="e.g. My Urgent Tickets" value={newViewName} onChange={(e) => setNewViewName(e.target.value)} />
          </FormField>
        </form>
      </Modal>
    </PageShell>
  );
}

export default Tickets;
